import { type ListingAttributeV2 } from '@kijiji/generated/graphql-types'
import { formatWholeNumber } from '@kijiji/number/formatWholeNumber'
import { type TFunction } from 'next-i18next'

import { getLocale, ROUTE_LOCALE } from '@/domain/locale'
import {
  BOOLEAN_NUMERIC_VALUES,
  BOOLEAN_TEXT_VALUES,
} from '@/features/attributes/constants/attributes'
import { formatDateTime } from '@/utils/date/formatDateTime'

export type LabelOverrideProps = (attribute: ListingAttributeV2, t: TFunction) => string

type LabelOverrideTypes =
  | 'BOOLEAN_NAME'
  | 'FORMAT_DATE'
  | 'FORMAT_NUMBER'
  | 'INCLUDED'
  | 'NOT_AVAILABLE'
  | 'VALUE_PLUS_NAME'
  | 'REPLACE_TRUE_VALUE_WITH_TRANSLATION'
type AttributesLabelOverride = { [x in LabelOverrideTypes]: LabelOverrideProps }

const isFalseValue = (value?: string) => {
  return !value || value === BOOLEAN_TEXT_VALUES.NO || value === BOOLEAN_NUMERIC_VALUES.NO
}

export const ATTRIBUTES_LABEL_OVERRIDE: AttributesLabelOverride = {
  REPLACE_TRUE_VALUE_WITH_TRANSLATION: ({ canonicalName, canonicalValues }, t) => {
    const value = canonicalValues?.[0]
    if (isFalseValue(value)) return ''

    return t(`attribute.label_override.${canonicalName}`)
  },
  FORMAT_DATE: ({ values }) => {
    const startDate = values[0]
    if (!startDate) return ''

    const endDate = values[1]
    const formattedDate = formatDateTime(startDate, endDate)

    return formattedDate
  },
  FORMAT_NUMBER: ({ values }) => {
    const value = parseInt(values[0] ?? '')

    if (typeof value !== 'number') return value
    const { routeLocale } = getLocale()
    const isEnglish = routeLocale === ROUTE_LOCALE.en

    return formatWholeNumber(value, isEnglish)
  },
  BOOLEAN_NAME: ({ name, canonicalValues }) => {
    const value = canonicalValues?.[0]
    if (isFalseValue(value)) return ''
    return name ?? ''
  },
  INCLUDED: ({ canonicalValues, name }, t) => {
    const value = canonicalValues?.[0]
    if (isFalseValue(value)) return ''

    return t(`listing:attribute.label_override.included`, { name })
  },
  NOT_AVAILABLE: ({ values }, t) => {
    const value = values[0]
    if (value === '0') return t('listing:attribute.label_override.not_available')

    return ''
  },
  VALUE_PLUS_NAME: ({ values, name }) => {
    return `${values[0]} ${name}`
  },
}
